import { useState } from "react";
import {
  Grid,
  TextField,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Loading,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "../../../../components";
import {
  ContasPagarService,
  notification,
  history,
} from "../../../../services";
import { responseErros, formatDate } from "../../../../utils";
import contaPagarValidator from "../middlewares/contaPagar.validator";
import desdobrarTituloValidator from "../middlewares/desdobrarTitulo.validator";

const tituloSecaoSx = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "#6c757d",
  marginBottom: 4,
};

const DesdobrarTitulo = ({
  contaPagar,
  meioPagamento,
  dadosPagamentoList,
  observacaoList,
  rateiosPlanoFinanceiroList,
  rateiosCentroCustoList,
  inputErros,
  setInputErro,
}) => {
  const [loading, setLoading] = useState(false);
  const [openDesdobrarTitulo, setOpenDesdobrarTitulo] = useState(false);
  const [desdobramentoTitulo, setDesdobramentoTitulo] = useState({
    tipoDesdobramento: "QUANTIDADE",
    tipoVencimento: "INTERVALO_DIAS",
  });
  const contasPagarService = new ContasPagarService();
  const diaVencimentoTitulo = contaPagar?.dataVencimento
    ? formatDate.format(contaPagar.dataVencimento).getDate()
    : "";

  const handleOpenDialog = () => {
    setOpenDesdobrarTitulo(true);
  };

  const handleCloseDialog = () => {
    setOpenDesdobrarTitulo(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "diaVencimento") {
      let numero = value.replace(/\D/g, "");
      if (numero.length > 2) numero = numero.slice(0, 2);
      let num = numero === "" ? undefined : parseInt(numero, 10);
      if (num !== undefined) {
        if (num === 0) num = undefined;
        else if (num > 31) num = 31;
      }
      setDesdobramentoTitulo((prev) => ({
        ...prev,
        [name]: num,
      }));
    } else {
      setDesdobramentoTitulo((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleChangeTipoVencimento = (event) => {
    const tipoVencimento = event.target.value;
    setInputErro([]);
    setDesdobramentoTitulo((prevState) => ({
      ...prevState,
      tipoVencimento,
      intervaloDias:
        tipoVencimento === "INTERVALO_DIAS"
          ? prevState.intervaloDias
          : undefined,
      diaVencimento:
        tipoVencimento === "DIA_FIXO"
          ? prevState?.diaVencimento || diaVencimentoTitulo || undefined
          : undefined,
    }));
  };

  const validarContaPagar = () => {
    setInputErro([]);
    contaPagarValidator
      .validate(
        { ...contaPagar, rateiosPlanoFinanceiroList, rateiosCentroCustoList },
        { abortEarly: false }
      )
      .then(() => {
        validarDesdobramentoTitulo();
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  const validarDesdobramentoTitulo = () => {
    setInputErro([]);
    desdobrarTituloValidator
      .validate(desdobramentoTitulo, { abortEarly: false })
      .then(() => {
        desdobrarTitulo();
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  const desdobrarTitulo = async () => {
    const body = {
      desdobramentoTitulo,
      contaPagar: {
        ...contaPagar,
        observacaoList,
        rateiosPlanoFinanceiroList,
        rateiosCentroCustoList,
        bancoCaixaId: meioPagamento?.bancoCaixaId,
        pagamentos: dadosPagamentoList,
      },
    };
    setLoading(true);
    const res = await contasPagarService.desdobrarTitulo(body);
    setLoading(false);
    if (!res.isAxiosError) {
      notification.cadastroSucesso();
      history.goBack();
    } else {
      responseErros(res);
    }
  };

  const handleBlurDia = () => {
    const current = desdobramentoTitulo?.diaVencimento;
    if (current !== undefined && current !== null && current !== "") {
      let num = Number(current);
      if (isNaN(num) || num < 1) num = 1;
      else if (num > 31) num = 31;
      setDesdobramentoTitulo((prev) => ({
        ...prev,
        diaVencimento: num,
      }));
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        className="mt-3"
        fullWidth
        color="primary"
        aria-controls={openDesdobrarTitulo ? "split-button-menu" : undefined}
        aria-expanded={openDesdobrarTitulo ? "true" : undefined}
        aria-haspopup="menu"
        onClick={handleOpenDialog}
        sx={{ height: "52px" }}
      >
        Desdobrar título
      </Button>
      <Dialog
        fullWidth
        maxWidth={"sm"}
        open={openDesdobrarTitulo}
        onClose={handleCloseDialog}
      >
        <CardHeader title="Desdobrar Título" className="m-2" />
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <span id="tipoDesdobramento" style={tituloSecaoSx}>
                Tipo de desdobramento
              </span>
              <RadioGroup
                aria-labelledby="tipoDesdobramento"
                name="tipoDesdobramento"
                value={desdobramentoTitulo?.tipoDesdobramento ?? ""}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="QUANTIDADE"
                  control={<Radio />}
                  label="Criar parcelas com o mesmo valor"
                />
                <FormControlLabel
                  value="VALOR"
                  control={<Radio />}
                  label="Dividir o valor total entre as parcelas"
                />
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <Divider variant="fullWidth" />
            </Grid>
            <Grid item xs={12}>
              <span id="tipoVencimento" style={tituloSecaoSx}>
                Vencimento das parcelas
              </span>
              <RadioGroup
                aria-labelledby="tipoVencimento"
                name="tipoVencimento"
                value={desdobramentoTitulo?.tipoVencimento ?? ""}
                onChange={handleChangeTipoVencimento}
              >
                <FormControlLabel
                  value="INTERVALO_DIAS"
                  control={<Radio />}
                  label="Intervalo de dias entre as parcelas"
                />
                <FormControlLabel
                  value="DIA_FIXO"
                  control={<Radio />}
                  label="Dia fixo de vencimento a cada mês"
                />
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <Divider variant="fullWidth" />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="qtdParcelas"
                name="qtdParcelas"
                label="Quantidade de parcelas"
                variant="outlined"
                required
                type="number"
                inputProps={{ min: 2 }}
                onChange={handleChange}
                value={desdobramentoTitulo?.qtdParcelas ?? ""}
                fullWidth
                error={inputErros.get("qtdParcelas")}
              />
            </Grid>
            <Grid item xs={6}>
              {desdobramentoTitulo?.tipoVencimento === "DIA_FIXO" ? (
                <TextField
                  id="diaVencimento"
                  name="diaVencimento"
                  label="Dia do vencimento"
                  variant="outlined"
                  required
                  type="text"
                  inputProps={{ maxLength: 2 }}
                  onChange={handleChange}
                  onBlur={handleBlurDia}
                  value={desdobramentoTitulo?.diaVencimento ?? ""}
                  fullWidth
                  error={inputErros.get("diaVencimento")}
                />
              ) : (
                <TextField
                  id="intervaloDias"
                  name="intervaloDias"
                  label="Intervalo de dias"
                  variant="outlined"
                  required
                  type="number"
                  inputProps={{ min: 1 }}
                  onChange={handleChange}
                  value={desdobramentoTitulo?.intervaloDias ?? ""}
                  fullWidth
                  error={inputErros.get("intervaloDias")}
                />
              )}
            </Grid>
            {desdobramentoTitulo?.tipoVencimento === "DIA_FIXO" && (
              <Grid item xs={12}>
                <span style={{ fontSize: 12, color: "#6c757d" }}>
                  Nos meses que não têm esse dia, a parcela vence no último dia
                  do mês.
                </span>
              </Grid>
            )}
          </Grid>
          <Loading loading={loading} />
        </DialogContent>
        <DialogActions sx={{ padding: "0.5rem 1.5rem 1.5rem !important" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={validarContaPagar}
          >
            <i
              className="ph-fill ph-check-circle"
              style={{ color: "white", fontSize: 22, marginRight: 10 }}
            ></i>
            Confirmar
          </Button>
          <Button
            color="padrao"
            variant="contained"
            style={{ marginLeft: 10 }}
            onClick={handleCloseDialog}
          >
            <i
              className="ph-fill ph-x-circle"
              style={{ fontSize: 22, marginRight: 10 }}
            ></i>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default DesdobrarTitulo;
